import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navigation } from "./Navigation";
import { Footer } from "./Footer";
import { Reveal } from "./motion-primitives";
import { RecommendationCard } from "./RecommendationComponents";
import { fetchRecommendations } from "./libraryApi";
import { useSeo } from "./useSeo";

// Editorial Discoverography collection — powered by the same Knowledge Library records.
export const DaNangAfterDark = () => {
  useSeo({
    title: "Da Nang After Dark — Two Hidden Cocktail Bars | Holideviser",
    description:
      "Two hidden cocktail bars in Da Nang worth finding — personally verified by Holideviser. An editorial collection from the Holideviser Knowledge Library.",
  });

  const [recs, setRecs] = useState(null);

  useEffect(() => {
    let active = true;
    fetchRecommendations({ destination: "Da Nang", category: "DRINK" })
      .then((data) => active && setRecs(data))
      .catch(() => active && setRecs([]));
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-ink text-cream" data-testid="collection-da-nang-after-dark">
      <Navigation />

      <header className="relative overflow-hidden border-b border-charcoal">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1500217052183-bc01eee1a74e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
            alt="A single cocktail lit in a dark bar (placeholder image)"
            className="h-full w-full object-cover opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
        </div>
        <div className="relative mx-auto max-w-[1000px] px-6 pb-16 pt-40 sm:px-10 sm:pb-20 sm:pt-48">
          <Reveal>
            <Link
              to="/discoverography"
              className="font-sans text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:text-teal"
            >
              ← Discoverography
            </Link>
          </Reveal>
          <Reveal delay={0.06}>
            <span className="mt-8 block font-sans text-xs uppercase tracking-[0.3em] text-teal">
              Da Nang · A Collection
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 font-serif text-5xl font-light leading-none tracking-tight text-cream sm:text-7xl">
              Da Nang After Dark
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-lg font-serif text-xl font-light italic text-cream/80">
              Two hidden cocktail bars worth finding.
            </p>
          </Reveal>
        </div>
      </header>

      <main className="mx-auto max-w-[1000px] px-6 py-16 sm:px-10 sm:py-24">
        <Reveal>
          <p className="max-w-2xl font-sans text-lg leading-relaxed text-sand">
            Da Nang rewards the traveller who looks past the obvious. These two rooms take a little finding —
            one down an alley, one behind a banh mi stall — and both trade a big night out for something quieter
            and more considered. We&apos;ve been to both.
          </p>
        </Reveal>

        {recs === null ? (
          <p className="mt-12 font-sans text-sm text-sand">Loading the collection…</p>
        ) : (
          <div className="mt-14 flex flex-col gap-12" data-testid="collection-list">
            {recs.map((rec, i) => (
              <Reveal key={rec.id} delay={i * 0.05}>
                <div className="flex items-baseline gap-4">
                  <span className="font-sans text-xs tracking-[0.2em] text-sand">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="h-px flex-1 bg-charcoal" />
                </div>
                <div className="mt-6">
                  <RecommendationCard rec={rec} defaultOpen />
                </div>
              </Reveal>
            ))}
          </div>
        )}

        <div className="mt-20 border-t border-charcoal pt-16">
          <p className="font-sans text-sm text-sand">
            Both places live in our{" "}
            <Link to="/destinations/da-nang" className="text-teal underline-offset-4 hover:underline">
              Da Nang edit
            </Link>
            , which we&apos;re growing one verified place at a time.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};
